import { useState, useEffect } from "react";
import { FiArrowLeft, FiArrowRight, FiX } from "react-icons/fi";
import { FaStar, FaRegStar } from "react-icons/fa";
import styles from "./Testimonials.module.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = ["Parent", "Student", "Tutor", "Guardian", "Other"];

const StarRating = ({ rating }) => (
  <div className={styles.stars}>
    {[1, 2, 3, 4, 5].map((i) =>
      i <= rating ? <FaStar key={i} /> : <FaRegStar key={i} />
    )}
  </div>
);

const StarPicker = ({ value, onChange }) => (
  <div className={styles.starPicker}>
    {[1, 2, 3, 4, 5].map((i) => (
      <button
        key={i}
        type="button"
        className={styles.starPickerBtn}
        onClick={() => onChange(i)}
        aria-label={`${i} star`}
      >
        {i <= value ? <FaStar /> : <FaRegStar />}
      </button>
    ))}
  </div>
);

function SubmitModal({ onClose }) {
  const [form, setForm] = useState({ reviewer_name: "", reviewer_role: "", review_text: "", rating: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rating) { setError("Please select a star rating."); return; }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/public/platform-reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setDone(true);
      } else {
        setError(data.message || "Something went wrong.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.modalClose} onClick={onClose} aria-label="Close">
          <FiX />
        </button>

        {done ? (
          <div className={styles.successState}>
            <div className={styles.successIcon}>🎉</div>
            <h3>Thank you!</h3>
            <p>Your review has been submitted and will appear on the site after admin approval.</p>
            <button className={styles.doneBtn} onClick={onClose}>Done</button>
          </div>
        ) : (
          <>
            <h2 className={styles.modalTitle}>Share Your Experience</h2>
            <p className={styles.modalSubtitle}>Tell others about your experience with Kooulu</p>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label>Your Name <span>*</span></label>
                <input
                  type="text"
                  placeholder="e.g. Sarah Mitchell"
                  value={form.reviewer_name}
                  onChange={(e) => setForm({ ...form, reviewer_name: e.target.value })}
                  required
                />
              </div>

              <div className={styles.field}>
                <label>Role</label>
                <select
                  value={form.reviewer_role}
                  onChange={(e) => setForm({ ...form, reviewer_role: e.target.value })}
                >
                  <option value="">Select your role (optional)</option>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <div className={styles.field}>
                <label>Rating <span>*</span></label>
                <StarPicker value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} />
              </div>

              <div className={styles.field}>
                <label>Your Review <span>*</span></label>
                <textarea
                  placeholder="What did you love about Kooulu?"
                  rows={4}
                  value={form.review_text}
                  onChange={(e) => setForm({ ...form, review_text: e.target.value })}
                  required
                />
              </div>

              {error && <p className={styles.formError}>{error}</p>}

              <button type="submit" className={styles.submitBtn} disabled={submitting}>
                {submitting ? "Submitting…" : "Submit Review"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [index, setIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/public/platform-reviews`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.data.length > 0) setReviews(data.data);
      })
      .catch(() => {});
  }, []);

  const current = reviews[index];
  const prev = () => setIndex((i) => (i === 0 ? reviews.length - 1 : i - 1));
  const next = () => setIndex((i) => (i === reviews.length - 1 ? 0 : i + 1));

  return (
    <>
      <section className={styles.testimonialsSection}>
        <div className={styles.testimonialsContainer}>
          <div className={styles.testimonialsText}>
            <h3 className={styles.sectionSubtitle}>Reviews & Testimonials</h3>
            <h2 className={styles.sectionsTitle}>What They Say?</h2>
            <p className={styles.sectionDesc}>
              Kooulu has received positive ratings from parents, tutors, and
              students around the world.
            </p>
            <p className={styles.sectionDesc}>
              Join our growing community and share your experience with us.
            </p>
            <button className={styles.shareExperienceBtn} onClick={() => setShowModal(true)}>
              <span>Share your experience</span>
              <div className={styles.arrowCircle}>
                <FiArrowRight />
              </div>
            </button>
          </div>

          <div className={styles.testimonialsCard}>
            <div className={styles.testimonialImgWrapper}>
              <picture style={{ display: "contents" }}>
                <source srcSet="/assets/images/testimonialsimg.webp" type="image/webp" />
                <img
                  src="/assets/images/testimonialsimg.png"
                  alt="Happy Family"
                  className={styles.testimonialImg}
                  width="658"
                  height="487"
                  loading="lazy"
                />
              </picture>
              <div className={styles.shapeLeft}></div>
              <div className={styles.shapeRight}></div>
            </div>

            <div className={styles.testimonialBox}>
              {/*
                Previously `current ? (...) : (<p>No reviews yet.</p>)` swapped the
                entire reviewText+testimonialFooter subtree for a single fallback <p>,
                and the arrow buttons were mounted/unmounted based on `reviews.length > 1`.
                `reviews` always starts [] on a fresh client mount (before the fetch in
                the useEffect resolves), while react-snap's prerendered HTML was captured
                once reviews had loaded — so the same "populated vs empty" structural
                mismatch that causes React error #418 applied here too. Fix: always
                render the same reviewText <p> and testimonialFooter div (falling back to
                empty/zero values), and always mount the arrow buttons, only toggling
                their visibility via style instead of removing them from the tree.
              */}
              <p className={styles.reviewText} style={current ? undefined : { color: "#aaa" }} suppressHydrationWarning>
                {current ? `"${current.review_text}"` : "No reviews yet."}
              </p>
              <div className={styles.testimonialFooter}>
                <div>
                  <span className={styles.author} suppressHydrationWarning>{current?.reviewer_name || ""}</span>
                  {current?.reviewer_role && (
                    <span className={styles.reviewerRole}> · {current.reviewer_role}</span>
                  )}
                </div>
                <StarRating rating={current?.rating || 0} />
              </div>

              <button
                className={`${styles.arrowBtns} ${styles.left}`}
                onClick={prev}
                aria-label="Previous"
                style={{ visibility: reviews.length > 1 ? "visible" : "hidden" }}
              >
                <FiArrowLeft />
              </button>
              <button
                className={`${styles.arrowBtns} ${styles.right}`}
                onClick={next}
                aria-label="Next"
                style={{ visibility: reviews.length > 1 ? "visible" : "hidden" }}
              >
                <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      {showModal && <SubmitModal onClose={() => setShowModal(false)} />}
    </>
  );
}
